import keyBy from 'lodash/keyBy';
import React, { useEffect, useState } from 'react';
import { LuSave, LuSquarePen, LuTrendingUp, LuX } from 'react-icons/lu';

import { Role } from '@ndvr/auth-api';
import { Account, AccountAllocationRequest, Allocation, GUID, ManagementType } from '@ndvr/portfolio-api';

import { useEditableCardGroup } from '@/components/EditableCardGroupContext';
import { Spinner } from '@/components/Spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useRequiredParams } from 'utils';
import { cn } from 'utils/cn';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useListAllocations } from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { useListStrategies } from 'views/portfolio-engine/allocation-builder/strategies/strategy-queries';
import { AccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useUpdateAccountAllocations, useUpdateAccountGroupAllocation } from 'views/portfolio/account-group-queries';
import { useAccountGroupAllocations } from 'views/portfolio/PortfolioPage/tabs/target-allocation/allocationUtils';

import { GrowthToggle } from './GrowthToggle';
import { PinnedAllocationView } from './PinnedAllocationView';
import { PortfolioAllocationView } from './PortfolioAllocationView';
import { useGrowthAllocationValidation } from './useGrowthAllocationValidation';

interface GrowthStrategiesCardProps {
    accountGroupProfile?: AccountGroupProfileWithAumIncluded;
    onUpdate?: (hasUnsavedChanges: boolean) => void;
    onAccountSelect?: (accountGUID: string | undefined) => void;
}

export const GrowthAllocationCard = ({ accountGroupProfile, onUpdate, onAccountSelect }: GrowthStrategiesCardProps) => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { isEditing, isEditingDisabled, setEditingCard } = useEditableCardGroup();
    const cardIsEditing = isEditing('target-allocation', 'growth');
    const cardIsEditingDisabled = isEditingDisabled('target-allocation', 'growth');

    const { data: allocations = [], isLoading: isLoadingAllocations } = useListAllocations(cardIsEditing);
    const assignableAllocations = keyBy(allocations, (allocation) => allocation.allocationGUID);

    const { data: strategies = [] } = useListStrategies();
    const { data: hasSupportRole, isPending: isLoadingPermissions } = useUserHasRole(Role.SUPPORT_USER);

    const updateAccountGroupAllocation = useUpdateAccountGroupAllocation(accountGroupGUID);
    const updateAccountAllocations = useUpdateAccountAllocations(accountGroupGUID);

    const { portfolioAllocation, accountAllocations } = useAccountGroupAllocations(accountGroupProfile);

    const defaultGrowthView = accountGroupProfile?.accountGroup.allocationGUID ? 'portfolio' : 'pinned';
    const [growthView, setGrowthView] = useState<'portfolio' | 'pinned'>(defaultGrowthView);

    const [portfolioAllocationGUID, setPortfolioAllocationGUID] = useState<GUID<Allocation> | undefined>();
    const [accounts, setAccounts] = useState<Account[]>([]);

    useEffect(() => {
        setGrowthView(defaultGrowthView);
    }, [defaultGrowthView]);

    useEffect(() => {
        if (!cardIsEditing) {
            setPortfolioAllocationGUID(accountGroupProfile?.accountGroup.allocationGUID);
        }
    }, [cardIsEditing, accountGroupProfile]);

    const hasPortfolioChanges =
        growthView === 'portfolio' && portfolioAllocationGUID !== accountGroupProfile?.accountGroup.allocationGUID;
    const hasAccountChanges =
        growthView === 'pinned' && JSON.stringify(accounts) !== JSON.stringify(accountGroupProfile?.accounts);
    const hasChanges = hasPortfolioChanges || hasAccountChanges;

    useEffect(() => {
        onUpdate?.(cardIsEditing && hasChanges);
    }, [cardIsEditing, hasChanges, onUpdate]);

    const handleCancelClick = () => {
        setPortfolioAllocationGUID(accountGroupProfile?.accountGroup.allocationGUID);
        setGrowthView(defaultGrowthView);
    };

    const handleSaveClick = () => {
        if (growthView === 'portfolio') {
            if (!portfolioAllocationGUID) {
                return;
            }
            updateAccountGroupAllocation.mutate(
                { allocationGUID: portfolioAllocationGUID },
                {
                    onSuccess: () => {
                        setEditingCard('target-allocation', null);
                    },
                },
            );
        } else if (growthView === 'pinned') {
            const accountAllocationRequests: AccountAllocationRequest[] = accounts.map((account) => ({
                accountGUID: account.accountGUID,
                managementType: account.managementType,
                allocationGUID: account.allocationGUID,
                cashEqSweep: account.managementType === ManagementType.AUTOMATIC ? account.cashEqSweep : undefined,
            }));

            updateAccountAllocations.mutate(accountAllocationRequests, {
                onSuccess: () => {
                    setEditingCard('target-allocation', null);
                },
            });
        }
    };

    const isSaving = updateAccountGroupAllocation.isPending || updateAccountAllocations.isPending;

    const { isSavingDisabled, tooltipMessage } = useGrowthAllocationValidation({
        growthView,
        portfolioAllocationGUID,
        accounts,
        hasChanges,
        isSaving,
    });

    const handlePortfolioAllocationChange = (allocationGUID: string | undefined) => {
        setPortfolioAllocationGUID(allocationGUID);
    };

    const handleGrowthViewChange = (selectedGrowthView: 'portfolio' | 'pinned') => {
        setGrowthView(selectedGrowthView);
    };

    return (
        <Card className="col-span-2 overflow-hidden rounded-t-xl">
            <CardHeader className={cn(cardIsEditing ? 'bg-[#d1b08e]' : 'bg-gray-200', 'pb-2')}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <LuTrendingUp />
                        <CardTitle className="text-lg font-semibold text-black">Growth Allocation</CardTitle>
                    </div>
                    <div className="ml-2 flex items-center gap-2">
                        <GrowthToggle
                            growthView={growthView}
                            onViewSwitch={handleGrowthViewChange}
                            isEditing={cardIsEditing}
                        />
                        {!cardIsEditing ? (
                            <button
                                onClick={() => setEditingCard('target-allocation', 'growth')}
                                disabled={cardIsEditingDisabled}
                                className="rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <LuSquarePen size={16} />
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => {
                                        setEditingCard('target-allocation', null);
                                        handleCancelClick();
                                    }}
                                    className="rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-black"
                                >
                                    <LuX size={16} />
                                </button>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={handleSaveClick}
                                                disabled={isSavingDisabled}
                                                className="rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {isSaving ? (
                                                    <div className="pointer-events-none">
                                                        <Spinner size="sm" />
                                                    </div>
                                                ) : (
                                                    <LuSave size={16} />
                                                )}
                                            </button>
                                        </TooltipTrigger>
                                        {isSavingDisabled && (
                                            <TooltipContent>
                                                <p>{tooltipMessage}</p>
                                            </TooltipContent>
                                        )}
                                    </Tooltip>
                                </TooltipProvider>
                            </>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-6">
                {growthView === 'portfolio' && (
                    <PortfolioAllocationView
                        cardIsEditing={cardIsEditing}
                        portfolioAllocationGUID={portfolioAllocationGUID}
                        portfolioAllocation={portfolioAllocation}
                        allocations={allocations}
                        isLoadingAllocations={isLoadingAllocations}
                        isLoadingPermissions={isLoadingPermissions}
                        onPortfolioAllocationChange={handlePortfolioAllocationChange}
                        canCreateSharedAllocation={hasSupportRole}
                        availableStrategies={strategies}
                    />
                )}
                {growthView === 'pinned' && (
                    <PinnedAllocationView
                        initialAccounts={(accountGroupProfile?.accounts ?? []).map((account) =>
                            defaultGrowthView === 'portfolio'
                                ? { ...account, managementType: ManagementType.MANUAL, allocationGUID: undefined }
                                : account,
                        )}
                        cardIsEditing={cardIsEditing}
                        allocations={allocations}
                        isLoadingAllocations={isLoadingAllocations}
                        isLoadingPermissions={isLoadingPermissions}
                        assignableAllocations={assignableAllocations}
                        accountAllocations={accountAllocations}
                        onAccountSelect={onAccountSelect}
                        onAccountsChange={setAccounts}
                        canCreateSharedAllocation={hasSupportRole}
                        availableStrategies={strategies}
                    />
                )}
            </CardContent>
        </Card>
    );
};
